import { CommonModule } from '@angular/common';
import { Component, inject, input, OnInit, signal } from '@angular/core';
import { HttpService } from '@app/service/http/http.service';
import { Gps } from '@app/shared/models/ride-history/gps.model';
import { Ride } from '@app/shared/models/ride-history/ride.model';
import { backendRestApis } from '@environments/backend-rest-apis';
import { environment } from '@environments/environment';

/**
 * Komponenta za prikaz Mapbox statične karte s rutom vožnje na temelju GPS podataka.
 * Prikazuje rutu vožnje na maloj (mini) mapi koristeći Mapbox Static API.
 */
@Component({
  selector: 'app-mini-map',
  imports: [CommonModule],
  templateUrl: './mini-map.component.html',
  styleUrl: './mini-map.component.css',
})
export class MiniMapComponent implements OnInit {
  private httpService = inject(HttpService);

  /**
   * Ulazni podatak - ID vožnje za koju se prikazuje ruta na mapi.
   */
  rideId = input.required<number>();

  /**
   * Signal koji sadrži dohvaćene GPS podatke za rutu.
   */
  gpsData = signal<Gps[] | undefined>(undefined);

  /**
   * URL za prikaz Mapbox statične karte s rutom.
   */
  staticMapUrl: string | null = null;

  /**
   * Maksimalna dozvoljena duljina URL-a za Mapbox Static API GET request.
   */
  private readonly MAX_URL_LENGTH = 8192;

  /**
   * OnInit lifecycle hook.
   */
  ngOnInit(): void {
    const rideIdValue = this.rideId();
    const rideUrl = `${environment.apiUrl}${backendRestApis.ride.getById(rideIdValue)}`;

    const rideSubscription = this.httpService.get<Ride>(rideUrl).subscribe({
      next: (ride) => {
        if (ride.mapImagePath) {
          // Predgenerirana slika postoji na serveru — koristi je
          this.staticMapUrl = `${environment.apiUrl}${backendRestApis.mapImages.getByMapImagePath(ride.mapImagePath)}`;
        } else if (this.isWithinCurrentRideWindow(ride.date)) {
          // Vožnja je unutar trenutnog "dana" (5:30 - 5:30) — generiraj uživo,
          // jer se ruta tijekom dana još mijenja
          this.staticMapUrl = `${environment.apiUrl}${backendRestApis.mapImages.generateCurrentOngoingMapImage}`;
        } else {
          // Rubni slučaj — prošla vožnja bez predgenerirane slike, nešto nije
          // u redu na backendu. Generiraj uživo kao fallback
          // i javi backendu da regenerira/spremi sliku za buduće dohvate.
          this.staticMapUrl = `${environment.apiUrl}${backendRestApis.mapImages.generateCurrentOngoingMapImage}`;
          this.reportMissingMapImage(rideIdValue, ride.date);
        }
      },
    });
    this.httpService.unsubscribeWithDestroyRef(rideSubscription);
  }

/**
 * Javlja backendu da je stara vožnja bez predgenerirane slike — backend
 * logira ovo kao upozorenje i može pokrenuti retry generiranje za tu vožnju.
 */
private reportMissingMapImage(rideId: number, rideDate: Date): void {
  const reportUrl = `${environment.apiUrl}${backendRestApis.mapImages.reportMissingMapImage(rideId)}`;
  const subscription = this.httpService.post(reportUrl, { rideId, rideDate }).subscribe({
    error: () => {
      console.warn(`Nije moguće prijaviti nedostajuću sliku za vožnju ${rideId}`);
    },
  });
  this.httpService.unsubscribeWithDestroyRef(subscription);
}

/**
   * Provjerava je li dani datum vožnje unutar trenutnog "ride day" prozora,
   * definiranog kao period od 5:30 ujutro do 5:30 ujutro sljedećeg dana
   * (ista logika kao na backendu u RideServiceImpl.defineNewRide()).
   *
   * @param rideDate datum vožnje
   * @returns true ako je vožnja dio trenutno aktivnog dana
   */
  private isWithinCurrentRideWindow(rideDate: Date): boolean {
    const ride = new Date(rideDate);
    const currentRideDate = this.resolveCurrentRideDate();

    return (
      ride.getFullYear() === currentRideDate.getFullYear() &&
      ride.getMonth() === currentRideDate.getMonth() &&
      ride.getDate() === currentRideDate.getDate()
    );
  }

  /**
   * Određuje na koji se kalendarski datum odnosi "trenutni" ride day,
   * koristeći 5:30 cutoff logiku — ako je trenutno vrijeme prije 5:30 ujutro,
   * trenutni ride day je još jučerašnji kalendarski datum.
   *
   * @returns datum trenutnog ride day-a
   */
  private resolveCurrentRideDate(): Date {
    const now = new Date();
    const fiveThirtyToday = new Date(now);
    fiveThirtyToday.setHours(5, 30, 0, 0);

    if (now < fiveThirtyToday) {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      return yesterday;
    }

    return now;
  }
}
